import type { LocationCreateSchema } from '@repo/schemas/location/locationCreate';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { usePostLocation } from '@repo/api/location/usePostLocation';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { locationCreateSchema } from '@repo/schemas/location/locationCreate';
import { CheckCircle2Icon, MapPinnedIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';

import LocationFormFields from '@/components/features/manage/manage-locations/LocationFormFields';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { Form } from '@/components/ui/form';
import { LoadingOverlay, LoadingOverlayLabel, LoadingOverlaySpinner } from '@/components/ui/loading-overlay';
import { LoadingSwap } from '@/components/ui/loading-swap';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

/**
 * Props for the CreateLocation component.
 * @property {boolean} open - Whether the dialog is open.
 * @property {(value: boolean) => void} onOpenChange - Callback to change dialog open state.
 */
interface CreateLocationProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
}

export default function CreateLocation(createLocationProps: CreateLocationProps) {
  const { open, onOpenChange } = createLocationProps;
  const { mutateAsync, isPending } = usePostLocation();

  const form = useForm<LocationCreateSchema>({
    mode: 'onBlur',
    defaultValues: {
      name: '',
      desc: '',
      coordinates: '',
      address: null,
    },
    resolver: standardSchemaResolver(locationCreateSchema),
  });

  const onSubmit = (data: LocationCreateSchema) => {
    mutateAsync(data)
      .then(() => {
        onClose();
      })
      .catch((error) => {
        toasterMessageHandler(baseErrorMessages, error);
      });
  };

  const onClose = () => {
    onOpenChange(false);
    form.reset();
  };

  const handleOnOpenChange = (value: boolean) => {
    if (!value) {
      onClose();
    } else {
      onOpenChange(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOnOpenChange}>
      <DialogContent className={'sm:max-w-148'}>
        <Form {...form}>
          <DialogBody>
            <LoadingOverlay isLoading={isPending}>
              <LoadingOverlaySpinner />
              <LoadingOverlayLabel>Trwa tworzenie lokalizacji...</LoadingOverlayLabel>
            </LoadingOverlay>
            <DialogHeader className={'border-border-primary items-center gap-2 border-b'}>
              <FeaturedIcon size={'lg'}>
                <MapPinnedIcon />
              </FeaturedIcon>
              <div className={'flex min-w-0 grow flex-col gap-0.5'}>
                <DialogTitle>Utwórz lokalizację</DialogTitle>
                <DialogDescription className={'text-muted-foreground text-xs'}>Wprowadź nazwę i współrzędne lokacji.</DialogDescription>
              </div>
            </DialogHeader>
            <form id={'create-location-form'} className={'flex grow flex-col gap-3 overflow-auto p-4'} onSubmit={form.handleSubmit(onSubmit)} noValidate>
              <LocationFormFields />
            </form>
          </DialogBody>
          <DialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
            <Button onClick={onClose} type={'button'} variant={'tertiary'} className={'w-full md:w-fit'}>
              Anuluj
            </Button>
            <Button disabled={isPending} form={'create-location-form'} type={'submit'} variant={'primary'} className={'w-full md:w-fit'}>
              <LoadingSwap isLoading={isPending} className="flex items-center gap-2">
                <CheckCircle2Icon />
                Zapisz
              </LoadingSwap>
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
